import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { API_BASE_URL } from '../config/apiConfig';
import { useMessage } from '../contexts/MessageContext';
import { FaRobot, FaCircleQuestion, FaCircleCheck, FaTag, FaBullseye, FaFlag, FaCheck } from 'react-icons/fa6';
import useLockBodyScroll from '../hooks/useLockBodyScroll';

const CAMPOS = ['pregunta', 'respuesta_correcta', 'respuesta_falsa1', 'respuesta_falsa2', 'respuesta_falsa3', 'explicacion', 'dificultad'];

// El enunciado y las cuatro respuestas son el contenido del concurso: no se
// reescriben de un clic por mucho que el agente proponga un texto. Se revisan y
// se escriben a mano. Solo dificultad y explicación admiten "Aceptar" directo.
const CAMPOS_SOLO_MANUAL = ['pregunta', 'respuesta_correcta', 'respuesta_falsa1', 'respuesta_falsa2', 'respuesta_falsa3'];
const CAMPOS_UN_CLIC = ['explicacion', 'dificultad'];

const esUnClic = (it) => Boolean(it.campo) && CAMPOS_UN_CLIC.includes(it.campo) && it.propuesto != null;
const esSoloManual = (it) => Boolean(it.campo) && CAMPOS_SOLO_MANUAL.includes(it.campo) && it.propuesto != null;

// Degradado de cabecera según la severidad de la propuesta.
const SEV_GRADIENT = {
  critico: 'from-rose-500 to-fuchsia-500 dark:from-rose-600 dark:to-fuchsia-600',
  aviso: 'from-amber-500 to-orange-500 dark:from-amber-600 dark:to-orange-600',
  info: 'from-sky-500 to-indigo-500 dark:from-sky-600 dark:to-indigo-600',
  default: 'from-slate-500 to-slate-600 dark:from-slate-600 dark:to-slate-700'
};

const SEV_PILL = {
  critico: 'bg-white/25 text-white',
  aviso: 'bg-white/25 text-white',
  info: 'bg-white/25 text-white'
};

const fieldLabelClass = 'block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-200 mb-1.5';
const baseInputClass = 'w-full px-2.5 sm:px-3 py-2 sm:py-2.5 text-xs sm:text-sm border border-gray-300 rounded-md bg-white text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-500 dark:text-white transition-colors duration-200';

// Tarjeta de información (icono + etiqueta + valor), al estilo de "Tu Pregunta Enviada".
const InfoCard = ({ icon, label, labelColor = 'text-slate-500 dark:text-slate-400', children, className = '' }) => (
  <div className={`rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 p-3 ${className}`}>
    <p className={`text-[11px] font-semibold uppercase tracking-wide flex items-center gap-1.5 ${labelColor}`}>
      <span>{icon}</span>{label}
    </p>
    <div className="mt-1 text-sm text-slate-800 dark:text-slate-100 break-words">{children}</div>
  </div>
);

/**
 * Modal de gestión de una propuesta del agente (estilo unificado con el resto de
 * modales del panel). Diff por campo, acciones Aceptar / Ignorar / Modificar,
 * comparación de duplicados y confirmaciones con panel propio (sin alerts).
 */
const PropuestaAgenteModal = ({ propuesta, isOpen, onClose, onResolved, preguntasLookup = {} }) => {
  const { showMessage } = useMessage();
  const items = useMemo(
    () => (Array.isArray(propuesta?.cambios_propuestos) ? propuesta.cambios_propuestos : []),
    [propuesta]
  );

  const [sel, setSel] = useState(() => {
    const s = {};
    items.forEach((it) => { if (it.firma) s[it.firma] = true; });
    return s;
  });
  const [editando, setEditando] = useState(false);
  const [valores, setValores] = useState(() => {
    const prop = {};
    items.forEach((it) => { if (it.campo && it.propuesto != null) prop[it.campo] = it.propuesto; });
    const init = {};
    CAMPOS.forEach((c) => { init[c] = prop[c] != null ? prop[c] : (propuesta?.[c] ?? ''); });
    return init;
  });
  const [enviando, setEnviando] = useState(false);
  const [confirmar, setConfirmar] = useState(null);
  const [comparando, setComparando] = useState(null);

  const dupComparable = useMemo(
    () => items.find((it) => it.comparar_id && !it.borrar) || null,
    [items]
  );
  // Si la propuesta toca el enunciado o alguna respuesta, no hay Aceptar: ese
  // contenido se reescribe a mano. Eliminar y resolver duplicados sí siguen
  // siendo de un clic, porque ahí no se está redactando nada.
  const proponeTexto = useMemo(() => items.some(esSoloManual), [items]);
  const hayAplicable = useMemo(
    () => items.some((it) => it.borrar)
      || Boolean(dupComparable)
      || (!proponeTexto && items.some(esUnClic)),
    [items, dupComparable, proponeTexto]
  );

  useLockBodyScroll(isOpen && Boolean(propuesta));

  if (!isOpen || !propuesta) return null;

  const sev = propuesta.severidad || 'info';
  const gradiente = SEV_GRADIENT[sev] || SEV_GRADIENT.default;

  const resolver = async (payload) => {
    setEnviando(true);
    try {
      const { data } = await axios.post(`${API_BASE_URL}/revisiones_resolver.php`, payload);
      showMessage(data.mensaje || (data.ok ? 'Hecho' : 'Error'), data.ok ? 'success' : 'error');
      if (data.ok) {
        if (typeof onResolved === 'function') onResolved();
        onClose();
      }
    } catch (e) {
      showMessage(e.response?.data?.mensaje || e.response?.data?.error || 'Error al procesar.', 'error');
    } finally {
      setEnviando(false);
      setConfirmar(null);
    }
  };

  const aceptar = () => {
    // Solo viajan las firmas de lo aplicable de un clic. El backend lo vuelve a
    // comprobar: la UI no es la garantía.
    const firmas = items.filter((it) => it.firma && sel[it.firma] && (it.borrar || esUnClic(it))).map((it) => it.firma);
    if (!firmas.length) {
      if (dupComparable) { eliminarDup(propuesta.pregunta_id, dupComparable.comparar_id); return; }
      showMessage('Marca al menos un cambio aplicable.', 'warning');
      return;
    }
    const borra = items.some((it) => it.borrar && sel[it.firma]);
    setConfirmar({
      mensaje: borra
        ? 'Vas a ELIMINAR esta pregunta de forma definitiva. ¿Confirmar?'
        : 'Se aplicarán los cambios seleccionados a la pregunta. ¿Confirmar?',
      onSi: () => resolver({ id: propuesta.id, accion: 'aceptar', firmas, confirmar_borrado: borra })
    });
  };

  const eliminarDup = (objetivoId, conservarId) => setConfirmar({
    mensaje: `Vas a ELIMINAR la pregunta #${objetivoId}` + (conservarId ? ` (se conservará #${conservarId})` : '') + '. ¿Confirmar?',
    onSi: () => resolver({ id: propuesta.id, accion: 'eliminar_duplicada', objetivo_id: objetivoId })
  });

  const ignorar = () => setConfirmar({
    mensaje: 'La propuesta se ignorará y el agente no volverá a sugerir lo mismo (salvo nueva información). ¿Confirmar?',
    onSi: () => resolver({ id: propuesta.id, accion: 'ignorar' })
  });


  const guardarManual = () => setConfirmar({
    mensaje: 'Se guardarán tus cambios manuales en la pregunta. ¿Confirmar?',
    onSi: () => resolver({ id: propuesta.id, accion: 'modificar', valores })
  });

  const comparada = comparando != null ? preguntasLookup[String(comparando)] : null;

  return (
    <motion.div
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-[1000]"
      onClick={onClose}
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.22, ease: 'easeOut' }}
    >
      <motion.div
        className="bg-white dark:bg-gray-800 dark:text-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-700"
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, y: 18, scale: 0.985 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 18, scale: 0.985 }}
        transition={{ duration: 0.24, ease: 'easeOut' }}
      >
        <div className="p-4 sm:p-7">
          {/* Cabecera con degradado según severidad */}
          <div className={`sticky top-0 z-10 -mx-4 sm:-mx-7 -mt-4 sm:-mt-7 px-4 sm:px-7 pt-5 pb-4 mb-6 bg-gradient-to-r ${gradiente} border-b border-white/25`}>
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-xl font-semibold text-white flex items-center gap-2"><FaRobot aria-hidden="true" /> Propuesta del agente</h3>
                {/* El motivo del aviso manda: es lo que hay que leer para decidir.
                    Solo si no hay resumen se cae al texto genérico. */}
                <p className="text-sm text-white/90 mt-1">
                  {propuesta.resumen || 'Revisa los cambios y decide: aceptar, ignorar o modificar.'}
                </p>
              </div>
              <button onClick={onClose} className="text-white/85 hover:text-white text-2xl leading-none" aria-label="Cerrar modal">&times;</button>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              <span className={`text-xs font-bold px-2.5 py-1 rounded-full uppercase ${SEV_PILL[sev] || SEV_PILL.info}`}>{sev}</span>
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-white/20 text-white">En proceso</span>
              <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-white/15 text-white">#{propuesta.pregunta_id} · {propuesta.motor || ''}</span>
            </div>
          </div>

          {/* Info de la pregunta */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
            <InfoCard icon={<FaCircleQuestion aria-hidden="true" />} label="Pregunta" labelColor="text-blue-600 dark:text-blue-300" className="sm:col-span-2">
              <span className="font-semibold">{propuesta.pregunta || '(pregunta eliminada)'}</span>
            </InfoCard>
            <InfoCard icon={<FaCircleCheck aria-hidden="true" />} label="Respuesta correcta" labelColor="text-emerald-600 dark:text-emerald-300">
              <span className="font-semibold">{propuesta.respuesta_correcta}</span>
            </InfoCard>
            <div className="grid grid-cols-2 gap-3">
              <InfoCard icon={<FaTag aria-hidden="true" />} label="Categoría">{propuesta.categoria || '—'}</InfoCard>
              <InfoCard icon={<FaBullseye aria-hidden="true" />} label="Dificultad">{propuesta.dificultad || '—'}</InfoCard>
            </div>
          </div>

          {/* Cambios propuestos. Un aviso puede no traer ninguno: entonces el
              motivo (ya visible en la cabecera) es todo lo que hay, y la única
              salida es corregirlo a mano o ignorarlo. */}
          {items.length > 0 ? (
            <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300 mb-2">Cambios propuestos</h4>
          ) : (
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 p-3 mb-3 text-sm text-slate-600 dark:text-slate-300">
              El agente no propone ningún cambio concreto para este aviso: hay que corregirlo a mano.
            </div>
          )}
          {items.map((it, i) => (
            <div key={i} className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 p-3 mb-2">
              {it.borrar ? (
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={Boolean(sel[it.firma])} onChange={() => setSel((p) => ({ ...p, [it.firma]: !p[it.firma] }))} className="h-4 w-4 accent-rose-600" />
                  <span className="font-bold text-rose-600 dark:text-rose-300">ELIMINAR pregunta</span>
                  <span className="text-xs text-slate-500">· {it.tipo}</span>
                </label>
              ) : it.campo ? (
                <>
                  <label className="flex items-center gap-2 text-sm">
                    {esSoloManual(it) ? (
                      <span className="text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200">
                        A mano
                      </span>
                    ) : (
                      <input type="checkbox" checked={Boolean(sel[it.firma])} onChange={() => setSel((p) => ({ ...p, [it.firma]: !p[it.firma] }))} className="h-4 w-4 accent-emerald-600" />
                    )}
                    <span className="font-semibold">{it.campo}</span>
                    <span className="text-xs text-slate-500">· {it.tipo} · {it.severidad}</span>
                  </label>
                  {it.motivo && <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{it.motivo}</p>}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2 text-sm">
                    <div className="rounded-md border border-rose-200 dark:border-rose-800/60 bg-rose-50 dark:bg-rose-950/30 p-2 text-rose-700 dark:text-rose-300 whitespace-pre-wrap break-words">
                      <span className="text-[10px] uppercase block opacity-70 mb-0.5">Actual</span>{String(it.actual ?? '')}
                    </div>
                    <div className="rounded-md border border-emerald-200 dark:border-emerald-800/60 bg-emerald-50 dark:bg-emerald-950/30 p-2 text-emerald-700 dark:text-emerald-300 whitespace-pre-wrap break-words">
                      <span className="text-[10px] uppercase block opacity-70 mb-0.5">Propuesto</span>{String(it.propuesto ?? '')}
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-sm">
                  <span className="text-slate-700 dark:text-slate-200 font-medium inline-flex items-center gap-1"><FaFlag aria-hidden="true" /> {it.tipo} · {it.severidad}</span>
                  <span className="text-xs text-slate-400 ml-1">(requiere corrección manual)</span>
                  {it.motivo && <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{it.motivo}</p>}
                </div>
              )}
              {it.comparar_id && (
                <button onClick={() => setComparando(it.comparar_id)} className="mt-2 text-xs text-indigo-600 dark:text-indigo-300 underline">
                  Ver las 2 preguntas comparadas (#{propuesta.pregunta_id} y #{it.comparar_id})
                </button>
              )}
            </div>
          ))}

          {Array.isArray(propuesta.fuentes) && propuesta.fuentes.length > 0 && (
            <div className="text-xs mb-3">
              <span className="text-slate-500">Fuentes: </span>
              {propuesta.fuentes.map((f, i) => (
                <a key={i} href={f.url} target="_blank" rel="noreferrer" className="text-blue-600 dark:text-blue-300 underline mr-2">{f.titulo || f.url}</a>
              ))}
            </div>
          )}

          {/* Comparación de duplicados */}
          {comparando != null && (
            <div className="mb-4 rounded-xl border border-indigo-300 dark:border-indigo-700 bg-indigo-50 dark:bg-indigo-950/40 p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-indigo-800 dark:text-indigo-200">Comparación de preguntas</span>
                <button onClick={() => setComparando(null)} className="text-xs text-slate-500 underline">Cerrar</button>
              </div>
              {comparada ? (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                    {[{ q: propuesta, id: propuesta.pregunta_id, label: 'Esta' }, { q: comparada, id: comparando, label: 'Comparada' }].map(({ q, id, label }) => (
                      <div key={id} className="rounded-lg border border-slate-200 dark:border-slate-700 p-2 bg-white dark:bg-slate-800">
                        <p className="text-[11px] uppercase text-slate-400">{label} (#{id})</p>
                        <p className="font-semibold break-words">{q.pregunta}</p>
                        <p className="text-xs mt-1 text-emerald-600 dark:text-emerald-300 inline-flex items-center gap-1"><FaCheck aria-hidden="true" /> {q.respuesta_correcta}</p>
                        <ul className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 list-disc list-inside">
                          {[q.respuesta_falsa1, q.respuesta_falsa2, q.respuesta_falsa3].filter(Boolean).map((f, i) => (
                            <li key={i} className="break-words">{f}</li>
                          ))}
                        </ul>
                        <p className="text-[11px] text-slate-400 mt-0.5">Dif: {q.dificultad}</p>
                        <button
                          onClick={() => eliminarDup(Number(id), Number(id) === Number(propuesta.pregunta_id) ? comparando : propuesta.pregunta_id)}
                          className="mt-2 bg-rose-600 text-white px-2.5 py-1 rounded text-xs hover:bg-rose-700"
                        >
                          Eliminar #{id}
                        </button>
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-2">Elimina la que sobre; se conservará la otra. La eliminación queda registrada en el historial.</p>
                </>
              ) : (
                <p className="text-xs text-slate-500">No se encontró la pregunta #{comparando} en la lista cargada.</p>
              )}
            </div>
          )}

          {/* Confirmación propia o acciones */}
          <div className="pt-4 mt-1 border-t border-gray-200 dark:border-gray-700">
            {confirmar ? (
              <div className="rounded-xl border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 p-3">
                <p className="text-sm text-amber-800 dark:text-amber-100 mb-2">{confirmar.mensaje}</p>
                <div className="flex gap-2">
                  <button disabled={enviando} onClick={confirmar.onSi} className="px-4 py-2 rounded-md bg-emerald-600 text-white font-medium hover:bg-emerald-700 disabled:opacity-50">Confirmar</button>
                  <button disabled={enviando} onClick={() => setConfirmar(null)} className="px-4 py-2 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:text-gray-100 dark:border-gray-500 dark:hover:bg-gray-600">Cancelar</button>
                </div>
              </div>
            ) : (
              // Tres botones como máximo, dos en el caso normal. Cerrar no está:
              // la X de la cabecera ya cierra sin hacer nada.
              <div className="flex flex-wrap items-center justify-end gap-3">
                {!hayAplicable && (
                  <span className="mr-auto text-xs text-slate-500 dark:text-slate-400">
                    {proponeTexto
                      ? 'El enunciado y las respuestas se reescriben a mano, no de un clic.'
                      : 'Este aviso no trae un cambio automático: corrígelo a mano.'}
                  </span>
                )}
                {hayAplicable && (
                  <button disabled={enviando} onClick={aceptar} className="px-5 py-2 rounded-md bg-emerald-600 text-white font-medium hover:bg-emerald-700 disabled:opacity-50">Aceptar</button>
                )}
                <button
                  disabled={enviando}
                  onClick={ignorar}
                  title="Quita el aviso y el agente no vuelve a proponer lo mismo."
                  className="px-4 py-2 rounded-md bg-slate-500 text-white font-medium hover:bg-slate-600 disabled:opacity-50"
                >
                  Ignorar
                </button>
                <button disabled={enviando} onClick={() => setEditando((v) => !v)} className="px-4 py-2 rounded-md bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-50">Modificar manualmente</button>
              </div>
            )}
          </div>

          {/* Editor manual (estilo del modal Modificar Pregunta) */}
          {editando && !confirmar && (
            <div className="mt-5">
              <div className="mb-4">
                <label className={fieldLabelClass}>Pregunta</label>
                <textarea rows={3} value={valores.pregunta} onChange={(e) => setValores((p) => ({ ...p, pregunta: e.target.value }))} className={`${baseInputClass} min-h-[100px] resize-y`} />
              </div>
              <div className="mb-4 p-4 rounded-xl border border-orange-100 dark:border-slate-600 bg-gradient-to-br from-orange-50/80 to-amber-50/70 dark:from-slate-900/30 dark:to-slate-800/40">
                <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300 mb-3">Respuestas</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-4">
                  <div>
                    <label className={`${fieldLabelClass} !text-emerald-600 dark:!text-emerald-400`}>Respuesta Correcta</label>
                    <input value={valores.respuesta_correcta} onChange={(e) => setValores((p) => ({ ...p, respuesta_correcta: e.target.value }))} className={baseInputClass} />
                  </div>
                  {['respuesta_falsa1', 'respuesta_falsa2', 'respuesta_falsa3'].map((c, i) => (
                    <div key={c}>
                      <label className={`${fieldLabelClass} !text-rose-600 dark:!text-rose-400`}>Respuesta Falsa {i + 1}</label>
                      <input value={valores[c]} onChange={(e) => setValores((p) => ({ ...p, [c]: e.target.value }))} className={baseInputClass} />
                    </div>
                  ))}
                </div>
              </div>
              <div className="mb-4">
                <label className={fieldLabelClass}>Dificultad</label>
                <select value={valores.dificultad} onChange={(e) => setValores((p) => ({ ...p, dificultad: e.target.value }))} className={baseInputClass}>
                  {['facil', 'medio', 'dificil'].map((d) => <option key={d} value={d} className="dark:bg-gray-600 dark:text-white">{d}</option>)}
                </select>
              </div>
              <div className="mb-4">
                <label className={fieldLabelClass}>Explicación (opcional)</label>
                <textarea rows={3} value={valores.explicacion} onChange={(e) => setValores((p) => ({ ...p, explicacion: e.target.value }))} className={`${baseInputClass} min-h-[110px] resize-y`} />
              </div>
              <div className="flex justify-end">
                <button disabled={enviando} onClick={guardarManual} className="px-5 py-2 rounded-md bg-emerald-600 text-white font-medium hover:bg-emerald-700 disabled:opacity-50">Guardar cambios</button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default PropuestaAgenteModal;
